import { getApiConfigNext } from '@y-sports/api-client';
import { TrainingApi } from '@y-sports/api-client/generated-next-management/src/apis/TrainingApi';
import type {
  AssignPlayersRequest,
  AttendanceMarkRequest,
  BulkAttendanceRequest,
  Training,
  TrainingAttendance,
  TrainingCreateRequest,
  TrainingNote,
  TrainingNoteCreateRequest,
  TrainingPaymentMode,
  TrainingPlayer,
  TrainingPlayerClaim,
  TrainingPlayerSettingsRequest,
  TrainingRecurringPayment,
  TrainingRecurringPaymentUpsertRequest,
  TrainingSession,
  TrainingSessionCreateRequest,
  TrainingSessionUpdateRequest,
  TrainingUpdateRequest,
} from '@y-sports/api-client/generated-next-management/src/models';
import { TrainingNoteCreateRequestToJSON } from '@y-sports/api-client/generated-next-management/src/models/TrainingNoteCreateRequest';
import { TrainingNoteFromJSON } from '@y-sports/api-client/generated-next-management/src/models/TrainingNote';
import { TrainingSessionFromJSON } from '@y-sports/api-client/generated-next-management/src/models/TrainingSession';
import { TrainingPlayerClaimFromJSON } from '@y-sports/api-client/generated-next-management/src/models/TrainingPlayerClaim';
import { Configuration as NextManagementConfiguration } from '@y-sports/api-client/generated-next-management/src/runtime';

export interface TrainingHistoryRecord extends TrainingAttendance {
  trainingId?: string;
  trainingName?: string;
  sessionDate?: Date;
  sessionTime?: string;
  trainingFee?: number;
  sessionExtraFee?: number;
  expectedFee?: number;
  paymentMode?: TrainingPaymentMode;
}

const getTrainingApi = async () => {
  const apiConfig = await getApiConfigNext();
  return new TrainingApi(new NextManagementConfiguration({
    basePath: apiConfig.basePath,
    headers: apiConfig.headers,
  }));
};

const requestTrainingNote = async (
  trainingId: string,
  noteId: string,
  method: 'PATCH' | 'DELETE',
  payload?: TrainingNoteCreateRequest,
): Promise<TrainingNote | void> => {
  const apiConfig = await getApiConfigNext();
  const response = await fetch(
    `${apiConfig.basePath}/trainings/${encodeURIComponent(trainingId)}/notes/${encodeURIComponent(noteId)}`,
    {
      method,
      headers: {
        ...apiConfig.headers,
        ...(payload ? { 'Content-Type': 'application/json' } : {}),
      },
      body: payload ? JSON.stringify(TrainingNoteCreateRequestToJSON(payload)) : undefined,
    },
  );

  if (!response.ok) {
    throw new Error(`Training note ${method} failed with status ${response.status}`);
  }

  if (method === 'DELETE' || response.status === 204) {
    return undefined;
  }

  return TrainingNoteFromJSON(await response.json());
};

const sortAttendance = (attendance: TrainingAttendance[]) => {
  return [...attendance].sort((left, right) => {
    const leftName = (left.playerName || '').toLowerCase();
    const rightName = (right.playerName || '').toLowerCase();
    const byName = leftName.localeCompare(rightName);

    if (byName !== 0) {
      return byName;
    }

    return (left.playerId || '').localeCompare(right.playerId || '');
  });
};

const getSessionFee = (trainingFee?: number | null, sessionExtraFee?: number | null) => {
  return Number(trainingFee || 0) + Number(sessionExtraFee || 0);
};

const formatDateOnlyForApi = (value: Date) => {
  const year = value.getFullYear();
  const month = `${value.getMonth() + 1}`.padStart(2, '0');
  const day = `${value.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const normalizeSessionDate = (value?: Date | string | null) => {
  if (!value) {
    return undefined;
  }

  const normalizedValue = typeof value === 'string' ? new Date(value) : value;

  return new Date(normalizedValue.getUTCFullYear(), normalizedValue.getUTCMonth(), normalizedValue.getUTCDate());
};

const normalizeTrainingSession = (session: TrainingSession): TrainingSession => ({
  ...session,
  sessionDate: normalizeSessionDate(session.sessionDate),
});

export const TrainingDataService = {
  getUserTrainings: async (email: string): Promise<Training[]> => {
    const api = await getTrainingApi();
    return api.getUserTrainings({ email });
  },

  getTrainings: async (): Promise<Training[]> => {
    const api = await getTrainingApi();
    return api.getAllTrainings();
  },

  getTraining: async (trainingId: string): Promise<Training> => {
    const api = await getTrainingApi();
    return api.getTrainingById({ id: trainingId });
  },

  getTrainingPlayers: async (trainingId: string): Promise<TrainingPlayer[]> => {
    const api = await getTrainingApi();
    return api.getTrainingPlayers({ id: trainingId });
  },

  getTrainingSessions: async (trainingId: string): Promise<TrainingSession[]> => {
    const api = await getTrainingApi();
    const sessions = await api.getTrainingSessions({ id: trainingId });
    return sessions.map(normalizeTrainingSession);
  },

  getTrainingRecurringPayments: async (trainingId: string): Promise<TrainingRecurringPayment[]> => {
    const api = await getTrainingApi();
    return api.getRecurringTrainingPayments({ id: trainingId });
  },

  getTrainingNotes: async (trainingId: string, publicOnly = false): Promise<TrainingNote[]> => {
    const api = await getTrainingApi();
    return api.getTrainingNotes({ id: trainingId, publicOnly });
  },

  getPlayerTrainingNotes: async (playerId: string, publicOnly = false): Promise<TrainingNote[]> => {
    const api = await getTrainingApi();
    return api.getPlayerTrainingNotes({ playerId, publicOnly });
  },

  getSessionAttendance: async (sessionId: string): Promise<TrainingAttendance[]> => {
    const api = await getTrainingApi();
    const attendance = await api.getSessionAttendance({ sessionId });
    return sortAttendance(attendance);
  },

  getTrainingAttendanceHistory: async (trainings: Training[]): Promise<TrainingHistoryRecord[]> => {
    if (trainings.length === 0) {
      return [];
    }

    const api = await getTrainingApi();
    const sessionsByTraining = await Promise.all(
      trainings
        .filter((training) => !!training.id)
        .map(async (training) => ({
          training,
          sessions: (await api.getTrainingSessions({ id: training.id! })).map(normalizeTrainingSession),
        }))
    );

    const historyBySession = await Promise.all(
      sessionsByTraining.flatMap(({ training, sessions }) =>
        sessions
          .filter((session) => !!session.id)
          .map(async (session) => {
            const attendance = await api.getSessionAttendance({ sessionId: session.id! });

            return attendance.map((record) => ({
              ...record,
              trainingId: training.id,
              trainingName: training.name,
              sessionDate: session.sessionDate,
              sessionTime: `${session.startTime} - ${session.endTime}`,
              trainingFee: Number(record.expectedBaseAmount ?? training.trainingFee ?? 0),
              sessionExtraFee: Number(session.sessionExtraFee || 0),
              expectedFee: Number(record.expectedAmount ?? getSessionFee(record.expectedBaseAmount ?? training.trainingFee, session.sessionExtraFee)),
              paymentMode: training.paymentMode,
            }));
          })
      )
    );

    return historyBySession.flat();
  },

  createTraining: async (payload: TrainingCreateRequest) => {
    const api = await getTrainingApi();
    return api.createTraining({ trainingCreateRequest: payload });
  },

  updateTraining: async (trainingId: string, payload: TrainingUpdateRequest) => {
    const api = await getTrainingApi();
    return api.updateTraining({
      id: trainingId,
      trainingUpdateRequest: payload,
    });
  },

  deleteTraining: async (trainingId: string) => {
    const api = await getTrainingApi();
    return api.deleteTraining({ id: trainingId });
  },

  assignPlayersToTraining: async (trainingId: string, payload: AssignPlayersRequest) => {
    const api = await getTrainingApi();
    return api.assignPlayersToTraining({
      id: trainingId,
      assignPlayersRequest: payload,
    });
  },

  updateTrainingPlayerSettings: async (
    trainingId: string,
    playerId: string,
    payload: TrainingPlayerSettingsRequest,
  ) => {
    const api = await getTrainingApi();
    return api.updateTrainingPlayerSettings({
      id: trainingId,
      playerId,
      trainingPlayerSettingsRequest: payload,
    });
  },

  claimTrainingPlayerInvitations: async (email: string): Promise<TrainingPlayerClaim[]> => {
    const api = await getTrainingApi();
    return api.claimTrainingPlayerInvitations({ email });
  },

  getPendingTrainingPlayerInvitations: async (email: string): Promise<TrainingPlayerClaim[]> => {
    const apiConfig = await getApiConfigNext();
    const response = await fetch(`${apiConfig.basePath}/users/${encodeURIComponent(email)}/training-player-invitations`, {
      headers: apiConfig.headers,
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch pending training player invitations: ${response.status}`);
    }

    const invitations = await response.json();
    return Array.isArray(invitations) ? invitations.map(TrainingPlayerClaimFromJSON) : [];
  },

  assignPlayersToSession: async (sessionId: string, payload: AssignPlayersRequest) => {
    const api = await getTrainingApi();
    return api.assignPlayersToSession({
      sessionId,
      assignPlayersRequest: payload,
    });
  },

  createTrainingSession: async (trainingId: string, payload: TrainingSessionCreateRequest) => {
    const apiConfig = await getApiConfigNext();
    const response = await fetch(`${apiConfig.basePath}/trainings/${trainingId}/sessions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(apiConfig.headers || {}),
      },
      body: JSON.stringify({
        ...payload,
        sessionDate: formatDateOnlyForApi(payload.sessionDate),
      }),
    });

    if (!response.ok) {
      throw new Error(`Create training session failed with status ${response.status}`);
    }

    const createdSession = TrainingSessionFromJSON(await response.json());
    return normalizeTrainingSession(createdSession);
  },

  updateTrainingSession: async (trainingId: string, sessionId: string, payload: TrainingSessionUpdateRequest) => {
    const api = await getTrainingApi();
    const updated = await api.updateTrainingSession({
      id: trainingId,
      sessionId,
      trainingSessionUpdateRequest: payload,
    });
    return normalizeTrainingSession(updated);
  },

  deleteTrainingSession: async (trainingId: string, sessionId: string) => {
    const api = await getTrainingApi();
    return api.deleteTrainingSession({ id: trainingId, sessionId });
  },

  markAttendance: async (sessionId: string, payload: AttendanceMarkRequest) => {
    const api = await getTrainingApi();
    return api.markAttendance({
      sessionId,
      attendanceMarkRequest: payload,
    });
  },

  bulkMarkAttendance: async (sessionId: string, payload: BulkAttendanceRequest) => {
    const api = await getTrainingApi();
    return api.bulkMarkAttendance({
      sessionId,
      bulkAttendanceRequest: payload,
    });
  },

  upsertTrainingRecurringPayment: async (trainingId: string, payload: TrainingRecurringPaymentUpsertRequest) => {
    const api = await getTrainingApi();
    return api.upsertRecurringTrainingPayment({
      id: trainingId,
      trainingRecurringPaymentUpsertRequest: payload,
    });
  },

  createTrainingNote: async (trainingId: string, payload: TrainingNoteCreateRequest) => {
    const api = await getTrainingApi();
    return api.createTrainingNote({
      id: trainingId,
      trainingNoteCreateRequest: payload,
    });
  },

  updateTrainingNote: async (trainingId: string, noteId: string, payload: TrainingNoteCreateRequest) => (
    requestTrainingNote(trainingId, noteId, 'PATCH', payload) as Promise<TrainingNote>
  ),

  deleteTrainingNote: async (trainingId: string, noteId: string) => {
    await requestTrainingNote(trainingId, noteId, 'DELETE');
  },
};
